import React, { useState, useEffect } from "react";
import useAuth from "./auth_hook";
import { APIContext } from "../context/api_hook";

type SubscriptionSettings = {
  "renews_at": Date
  "product_name": string
  "status": string
  "customer_portal_url": string
  "update_payment_method_url": string
  "customer_portal_update_subscription_url": string
}

// This hook checks if a user has a valid subscription
const usePayment = () => {
  const { user, isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const [hasPaid, setHasPaid] = useState(false);
  // Start in a loading state until the first check is completed
  const [isCheckingPayment, setIsCheckingPayment] = useState(true);
  const [checkout_url, setCheckoutUrl] = useState(null);
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);
  const [subscriptionSettings, setSubscriptionSettings] = useState<SubscriptionSettings>();
  const [isLoadingSubscriptionSettings, setIsLoadingSubscriptionSettings] = useState(false);
  const { fetch_api } = React.useContext(APIContext);
  const checkoutUrlFetched = React.useRef(false);


  useEffect(() => {
    // Define the async function to check payment
    const checkPaymentStatus = async () => {
      // If not authenticated, there's no payment status to check.
      if (!isAuthenticated) {
        setHasPaid(false);
        setIsCheckingPayment(false);
        return;
      }

      setIsCheckingPayment(true);
      try {
        const options = {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({})
        };

        const response = await fetch_api("/api/user_subscription", options);
        const subscription_info = await response;
        console.log({ subscription_info });
        if (subscription_info.subscription.role === "admin") {
          setHasPaid(true);
        } else {
          setHasPaid(subscription_info.subscription.is_paying_user);
        }
      } catch (error) {
        console.error("Error checking payment status:", error);
        setHasPaid(false);
      } finally {
        setIsCheckingPayment(false);
      }
    };


    // Run the check only when auth is no longer loading
    if (!isAuthLoading) {
      checkPaymentStatus();
    }
  }, [user, isAuthenticated, isAuthLoading, fetch_api]);

  const getCheckoutUrl = async (variant: any) => {
    if (checkoutUrlFetched.current || isPaymentLoading) {
      return;
    }
    setIsPaymentLoading(true);
    try {
      const options = {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          variant: variant
        })
      };

      const response = await fetch_api("/api/get_lemonsqueezy_checkout_urls", options);
      const url = response.checkout_url;
      console.log({ url });
      setCheckoutUrl(url);
      checkoutUrlFetched.current = true;
    } catch (error) {
      console.error("Error getting custom checkout URLs:", error);
    } finally {
      setIsPaymentLoading(false);
    }
  };

  useEffect(() => {
    console.log({ checkout_url });
  }, [checkout_url]);

  const getUserSubscriptionSettings = async () => {
    if (isLoadingSubscriptionSettings) {
      return;
    }
    setIsLoadingSubscriptionSettings(true);
    try {
      const options = {
        method: "GET",
        headers: { "Content-Type": "application/json" }
      };

      const response = await fetch_api("/api/user_subscription_settings", options);
      const settings = response.settings;
      console.log({ settings });
      setSubscriptionSettings(settings);
    } catch (error) {
      console.error("Error getting subscription URLs:", error);
    } finally {
      setIsLoadingSubscriptionSettings(false);
    }
  };

  return {
    hasPaid,
    isCheckingPayment,
    isPaymentLoading,
    getCheckoutUrl,
    checkout_url,
    getUserSubscriptionSettings,
    subscriptionSettings,
    isLoadingSubscriptionSettings
  };
};

export default usePayment;
